/* eslint-disable eqeqeq */
import { configure, makeAutoObservable, runInAction } from "mobx";
import { apiClient } from "../base-classes/apiBaseClass";
import notificationState from "./notification-state";
import pdfServiceStore from "../../user/mobx/pdfState";
import subscriptionState from "./subscription-state";
// import { redirect } from "react-router-dom";
enum UserRoles {
    Admin = "admin",
    User = "user",
}

enum LocalStorageKeys {
    User = "web2pdf-user",
}

export interface IUser {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    activationToken: string;
    emailVerifiedAt: string | null;
    lastLogin: string | null;
    createdAt?: string;
    updatedAt?: string;
    deleted?: boolean;
    status?: string;
    authToken: string;
    role: string;
    address_id?: string;
    customer_id?: string;
    business_id?: string;
    profileImage?: string;
    selected_plan?: number | string | null;
}

interface LoginResponse {
    status?: number;
    message?: string;
    errors?: string[];
    data?: {
        user: IUser;
        token: string;
    };
}

interface SignUpResponse {
    status?: number;
    message?: string;
    errors?: string[];
}

interface ApiResponse {
    status?: number;
    message?: string;
    errors?: string[];
}


class Auth {
    private _user?: IUser | null | undefined;

    constructor() {
        this.synchronizeUserState();
        // Changed from "never" to "always" for better state management discipline
        configure({ enforceActions: "always" });
        makeAutoObservable(this);
    }

    /* ============= Computed Properties ============= */
    get currentUser(): IUser | null | undefined { return this._user; }

    get activeRole(): string {
        return this.isLoggedIn()
            ? this.currentUser!.role : "log-out";
    }

    get isUser(): boolean {
        return this.activeRole === UserRoles.User;
    }

    get isAdmin(): boolean {
        return this.activeRole === UserRoles.Admin;
    }

    get isGuest(): boolean {
        return !this.isLoggedIn();
    }

    /* ============= Public Functions ============= */
    isLoggedIn(): boolean {
        return !!this.currentUser;
    }
    clearPackageId(): void {
        runInAction(() => {
            if (this._user) {
                this._user = {
                    ...this._user,
                    selected_plan: null
                };
            }
        });
        this.synchronizeUserState();
    }
    async doLogin(values: { email: string; password: string }): Promise<{ status: number; error: string }> {
        pdfServiceStore.resetState();
        let status = 0, error = "";
        try {
            const response = await apiClient.post<any, LoginResponse>("login", values);
            console.log(response.message);
            if ((response?.errors && response.errors[0]) || response.status === 0) {
                error = response?.errors && response.errors[0] ?
                    response.errors[0] : (response.message || "Login failed");
                notificationState.addToasterError(error);
            } else if (response.data?.user && response.data?.token) {
                notificationState.addToasterSuccess("Login Successfully");
                runInAction(() => {
                    this.setUser({
                        ...response.data!.user,
                        authToken: response.data!.token,
                    });
                });
                status = 1;
            }
        } catch (e: unknown) {
            error = (e as Error).message || "Error...";
            notificationState.addToasterError(error);
        }
        return { status, error };
    }

    // pass useNavigation function to this...
    async navigateToRole(navigateFn: (path: string) => void | Promise<void>, returnUrl?: string): Promise<void> {
        const navAppRole: Record<string, string> = {
            user: "/",
            admin: "/dashboard",
        };
        const targetUrl = returnUrl || (navAppRole[this.activeRole] ?? "/auth/login");
        await navigateFn(targetUrl);
    }
    async navigateToRolePayment(navigateFn: (path: string) => void | Promise<void>, returnUrl?: string): Promise<void> {
        const navAppRole: Record<string, string> = {
            user: "/subscriptions",
            admin: "/dashboard",
        };
        const targetUrl = returnUrl || (navAppRole[this.activeRole] ?? "/auth/login");
        await navigateFn(targetUrl);
    }

    // async doSignUp(values) {
    //     pdfServiceStore.resetState();
    //     let status = 0, error = "";
    //     try {
    //         const response = await apiClient.post("register",

    //             { ...values, agreeToTerms: undefined, confirmPassword: undefined });

    //         if ((response?.errors && response.errors[0]) || response.status == 0) {
    //               status = 0;
    //             // this.setUser(response.data);
    //             error = response?.errors && response.errors[0] ?
    //                 response?.errors[0] : response.message;
    //             notificationState.addToasterError(error);
    //         } else {
    //             status = 1;
    //             notificationState.addToasterSuccess(response?.message ||
    //                 "Registration has been completed...");
    //             // this.setUser(response.data);
    //         }
    //     } catch (e: any) {
    //         error = e.message || "Error..."
    //     }
    //     return { status, error };
    // }
    async doSignUp(values: { 
        package_id?: number | string; 
        source?: string; 
        email: string; 
        password: string; 
        firstName: string; 
        lastName: string;
        agreeToTerms?: boolean;
        confirmPassword?: string;
        [key: string]: unknown;
    }): Promise<{ status: number; error: string }> {
        pdfServiceStore.resetState();
        let status = 0, error = "";

        try {
            // Extract package_id and source from values
            const { package_id, source, ...restValues } = values;

            // Construct the query parameters
            const queryParams = new URLSearchParams({
                selected_plan: String(package_id || ''),
                source: source || 'website' // Default to 'website' if not provided
            }).toString();

            // Make the API request with query parameters
            const response = await apiClient.post<SignUpResponse>(`register?${queryParams}`, {
                ...restValues, // Pass the rest of the values excluding package_id and source
                agreeToTerms: undefined, // Remove unnecessary fields
                confirmPassword: undefined
            });

            // Handle the response
            if ((response?.errors && response.errors[0]) || response.status === 0) {
                status = 0;
                error = response?.errors && response.errors[0] ?
                    response.errors[0] : (response.message || "Registration failed");
                notificationState.addToasterError(error);
            } else {
                status = 1;
                notificationState.addToasterSuccess(response?.message ||
                    "Registration has been completed...");
            }
        } catch (e: unknown) {
            error = (e as Error).message || "Error...";
        }

        return { status, error };
    }


    async doVerifyAccount(data: { token: string; email: string }): Promise<{ status: number; error: string }> {
        let status = 0, error = "";
        try {
            const response = await apiClient.post<ApiResponse>("verifyemail", data);
            status = response.status || 0;
            if (response.errors && response.errors[0]) {
                notificationState.addToasterError(response.errors[0]);
            } else {
                notificationState.addToasterSuccess(response?.message ||
                    "Account has been verified...");
                status = 1;
            }
        } catch (e: unknown) {
            error = (e as Error).message || "Error...";
            notificationState.addToasterError(error);
        }
        return { status, error };
    }

    async doVerifyEmail(data: { email: string }): Promise<{ status: number; error: string }> {
        let status = 0, error = "";
        try {
            const response = await apiClient.post<ApiResponse>("reset-email-link", data);
            status = response.status || 0;
            if (response.errors && response.errors[0]) {
                notificationState.addToasterError(response.errors[0]);
            } else {
                notificationState.addToasterSuccess(response?.message || "Success...");
                status = 1;
            }
        } catch (e: unknown) {
            error = (e as Error).message || "Error...";
            notificationState.addToasterError(error);
        }
        return { status, error };
    }

    async doForgotPassword(data: { email: string }): Promise<{ status: number; error: string }> {
        let status = 0, error = "";
        try {
            const response = await apiClient.post<ApiResponse>("forget-password", data);
            status = response.status || 0;
            if (response.errors && response.errors[0]) {
                notificationState.addToasterError(response.errors[0]);
            } else {
                notificationState.addToasterSuccess(response?.message || "Success...");
                status = 1;
            }
        } catch (e: unknown) {
            error = (e as Error).message || "Error...";
            notificationState.addToasterError(error);
        }
        return { status, error };
    }

    async resetPassword(token: string | null, values: { password: string }): Promise<{ status: number; error: string; isTokenExpired?: boolean }> {
        let status = 0, error = "", isTokenExpired = false;
        
        if (!token) {
            error = "Reset token is required";
            notificationState.addToasterError(error);
            return { status, error, isTokenExpired };
        }
        
        try {
            const response = await apiClient.post<ApiResponse>("reset-password",
                { token, password: values.password });
            status = response.status || 0;
            if (response.errors && response.errors[0]) {
                error = response.errors[0];
                // Check if the error is related to token expiration or invalid token
                if (error.toLowerCase().includes('expire') || 
                    error.toLowerCase().includes('used') || 
                    error.toLowerCase().includes('invalid')) {
                    isTokenExpired = true;
                }
                notificationState.addToasterError(error);
            } else {
                notificationState.addToasterSuccess(response?.message || "Success...");
                status = 1;
            }
        } catch (e: unknown) {
            error = (e as Error).message || "Error...";
            notificationState.addToasterError(error);
        }
        return { status, error, isTokenExpired };
    }

    doLogout(): void {
        window.location.href = '/auth/login?preaction=logout';
    }
    logout(): void {
        pdfServiceStore.resetState();
        this.clearUserState();
        notificationState.addToasterSuccess("Logout Successfully");
    }

    clearUserState(): void {
        try {
            localStorage.removeItem(LocalStorageKeys.User);
            runInAction(() => {
                this._user = null;
            });
        } catch (error) {
            console.error("Error: Unable to clear user state", error);
        }
    }

    getAllRoles(): typeof UserRoles {
        return UserRoles;
    }


    /* ============= Private Functions ============= */
    private setUser(user: IUser): void {
        runInAction(() => {
            this._user = user;
        });
        this.synchronizeUserState();
    }

    public synchronizeUserState(): void {
        try {
            if (this.currentUser) {
                const userJson = JSON.stringify(this.currentUser);
                localStorage.setItem(LocalStorageKeys.User, userJson);
            }
        } catch (error) {
            console.error("Error: Unable to set user in local storage", error);
        }

        try {
            const storedUser = localStorage.getItem(LocalStorageKeys.User);
            if (storedUser) {
                runInAction(() => {
                    this._user = JSON.parse(storedUser) as IUser;
                });
            }
        } catch (error) {
            console.error("Error: No user found in local storage", error);
        }
    }

    /* ========= API Request functions ============= */
}

const auth = new Auth();

export default auth;


